package com.evoapps.evoshift.domain.spreadsheet

/**
 * SH-123: a raw DEFLATE (RFC 1951) decoder in plain Kotlin. Modelled on zlib's `puff`: stored,
 * fixed and dynamic Huffman blocks. [maxOutput] bounds what one entry may expand to (a "zip bomb"
 * fails instead of filling the memory). SH-214: the platforms inflate natively ([inflateRaw]);
 * this one is the browser's fallback where DecompressionStream("deflate-raw") is missing, and the
 * reference the platform inflaters are tested against.
 */
internal object Inflate {

    class InvalidDataException(message: String) : IllegalArgumentException(message)

    fun inflate(input: ByteArray, offset: Int = 0, length: Int = input.size - offset, maxOutput: Int = DEFAULT_MAX_OUTPUT): ByteArray =
        State(input, offset, offset + length, maxOutput).run()

    const val DEFAULT_MAX_OUTPUT = 32 * 1024 * 1024
    internal const val TOO_LARGE = "Entry too large"

    private const val MAX_BITS = 15
    private val LENGTH_BASE = intArrayOf(3, 4, 5, 6, 7, 8, 9, 10, 11, 13, 15, 17, 19, 23, 27, 31, 35, 43, 51, 59, 67, 83, 99, 115, 131, 163, 195, 227, 258)
    private val LENGTH_EXTRA = intArrayOf(0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 1, 1, 2, 2, 2, 2, 3, 3, 3, 3, 4, 4, 4, 4, 5, 5, 5, 5, 0)
    private val DIST_BASE = intArrayOf(
        1, 2, 3, 4, 5, 7, 9, 13, 17, 25, 33, 49, 65, 97, 129, 193, 257, 385, 513, 769, 1025, 1537, 2049, 3073,
        4097, 6145, 8193, 12289, 16385, 24577
    )
    private val DIST_EXTRA = intArrayOf(0, 0, 0, 0, 1, 1, 2, 2, 3, 3, 4, 4, 5, 5, 6, 6, 7, 7, 8, 8, 9, 9, 10, 10, 11, 11, 12, 12, 13, 13)
    private val CODE_LENGTH_ORDER = intArrayOf(16, 17, 18, 0, 8, 7, 9, 6, 10, 5, 11, 4, 12, 3, 13, 2, 14, 1, 15)

    /** A canonical Huffman code: how many symbols have each length, and the symbols in code order. */
    private class Huffman(lengths: IntArray, count: Int) {
        val counts = IntArray(MAX_BITS + 1)
        val symbols = IntArray(count)

        init {
            for (i in 0 until count) counts[lengths[i]]++
            val offsets = IntArray(MAX_BITS + 1)
            for (len in 1 until MAX_BITS) offsets[len + 1] = offsets[len] + counts[len]
            for (symbol in 0 until count) if (lengths[symbol] != 0) symbols[offsets[lengths[symbol]]++] = symbol
        }
    }

    private val FIXED_LITERALS: Huffman by lazy {
        val lengths = IntArray(288) { i -> when { i < 144 -> 8; i < 256 -> 9; i < 280 -> 7; else -> 8 } }
        Huffman(lengths, 288)
    }
    private val FIXED_DISTANCES: Huffman by lazy { Huffman(IntArray(30) { 5 }, 30) }

    private class State(val input: ByteArray, var position: Int, val end: Int, val maxOutput: Int) {
        private var bitBuffer = 0
        private var bitCount = 0
        private var out = ByteArray(minOf(maxOf(1024, (end - position) * 4), maxOutput.coerceAtLeast(1)))
        private var size = 0

        fun run(): ByteArray {
            do {
                val last = bits(1) == 1
                when (bits(2)) {
                    0 -> stored()
                    1 -> codes(FIXED_LITERALS, FIXED_DISTANCES)
                    2 -> dynamic()
                    else -> throw InvalidDataException("Invalid block type")
                }
            } while (!last)
            return out.copyOf(size)
        }

        private fun bits(need: Int): Int {
            var value = bitBuffer
            while (bitCount < need) {
                if (position >= end) throw InvalidDataException("Unexpected end of data")
                value = value or ((input[position++].toInt() and 0xFF) shl bitCount)
                bitCount += 8
            }
            bitBuffer = value ushr need
            bitCount -= need
            return value and ((1 shl need) - 1)
        }

        private fun emit(byte: Byte) {
            if (size == out.size) {
                if (size >= maxOutput) throw InvalidDataException(TOO_LARGE)
                out = out.copyOf(minOf(maxOutput, maxOf(size * 2, 1024)))
            }
            out[size++] = byte
        }

        private fun stored() {
            bitBuffer = 0
            bitCount = 0
            if (position + 4 > end) throw InvalidDataException("Unexpected end of data")
            val len = (input[position].toInt() and 0xFF) or ((input[position + 1].toInt() and 0xFF) shl 8)
            val nlen = (input[position + 2].toInt() and 0xFF) or ((input[position + 3].toInt() and 0xFF) shl 8)
            position += 4
            if (len != (nlen.inv() and 0xFFFF)) throw InvalidDataException("Invalid stored block")
            if (position + len > end) throw InvalidDataException("Unexpected end of data")
            repeat(len) { emit(input[position++]) }
        }

        private fun decode(code: Huffman): Int {
            var value = 0
            var first = 0
            var index = 0
            for (len in 1..MAX_BITS) {
                value = value or bits(1)
                val count = code.counts[len]
                if (value - count < first) return code.symbols[index + (value - first)]
                index += count
                first += count
                first = first shl 1
                value = value shl 1
            }
            throw InvalidDataException("Invalid Huffman code")
        }

        private fun codes(literals: Huffman, distances: Huffman) {
            while (true) {
                val symbol = decode(literals)
                when {
                    symbol < 256 -> emit(symbol.toByte())
                    symbol == 256 -> return
                    else -> {
                        val lengthIndex = symbol - 257
                        if (lengthIndex >= 29) throw InvalidDataException("Invalid length code")
                        val length = LENGTH_BASE[lengthIndex] + bits(LENGTH_EXTRA[lengthIndex])
                        val distIndex = decode(distances)
                        if (distIndex >= 30) throw InvalidDataException("Invalid distance code")
                        val distance = DIST_BASE[distIndex] + bits(DIST_EXTRA[distIndex])
                        if (distance > size) throw InvalidDataException("Distance too far back")
                        repeat(length) { emit(out[size - distance]) }
                    }
                }
            }
        }

        private fun dynamic() {
            val literalCount = bits(5) + 257
            val distanceCount = bits(5) + 1
            val codeLengthCount = bits(4) + 4
            if (literalCount > 286 || distanceCount > 30) throw InvalidDataException("Too many codes")
            val lengths = IntArray(320)
            for (i in 0 until codeLengthCount) lengths[CODE_LENGTH_ORDER[i]] = bits(3)
            val lengthCode = Huffman(lengths.copyOf(19), 19)
            val all = IntArray(literalCount + distanceCount)
            var index = 0
            while (index < literalCount + distanceCount) {
                var symbol = decode(lengthCode)
                if (symbol < 16) {
                    all[index++] = symbol
                } else {
                    var repeatLength = 0
                    when (symbol) {
                        16 -> {
                            if (index == 0) throw InvalidDataException("Repeat with no first length")
                            repeatLength = all[index - 1]
                            symbol = 3 + bits(2)
                        }
                        17 -> symbol = 3 + bits(3)
                        else -> symbol = 11 + bits(7)
                    }
                    if (index + symbol > literalCount + distanceCount) throw InvalidDataException("Too many lengths")
                    repeat(symbol) { all[index++] = repeatLength }
                }
            }
            if (all[256] == 0) throw InvalidDataException("No end-of-block code")
            val literals = Huffman(all.copyOfRange(0, literalCount), literalCount)
            val distances = Huffman(all.copyOfRange(literalCount, literalCount + distanceCount), distanceCount)
            codes(literals, distances)
        }
    }
}

/** [Inflate] with the platforms' failures: beyond [maxOutput] is too large, anything else corrupt. */
internal fun inflatePure(data: ByteArray, maxOutput: Int): ByteArray = try {
    Inflate.inflate(data, maxOutput = maxOutput)
} catch (e: Inflate.InvalidDataException) {
    if (e.message == Inflate.TOO_LARGE) throw inflatedTooLarge() else throw SpreadsheetException(SpreadsheetProblem.CORRUPT, e.message)
}
